<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import Button from "~/components/dumb/Button.vue";
import Input from "~/components/dumb/Input.vue";
import { CheckIcon, XIcon } from "~/icons";
import { ButtonStyle, IconSize } from "~/shared/enums";
import type { Option, PropertyAnySelect, ValidationFunctionResult } from "~/shared/types";
import { useDataStore } from "~/stores";
import { layoutStable } from "~/utils/wait";

const props = defineProps<{
  option: Option;
  property: PropertyAnySelect;
  adding?: boolean;
}>();

const emit = defineEmits<{
  rename: [value: string];
  close: [];
}>();

const dataStore = useDataStore();

const options = computed(() => dataStore.getOptionList(props.property));

const titleInput = ref<InstanceType<typeof Input> | null>(null);

const showButtons = computed(() => props.adding && titleInput.value?.value !== props.option.title);

const invalidOptionTitle = (title: string): ValidationFunctionResult => {
  const trimmed = title.trim();
  if (trimmed === "") {
    return { isValid: false, error: "" };
  }

  const { parentDuid } = props.option;
  const isDuplicate = options.value.some(
    (e) => e.duid !== props.option.duid && e.title === trimmed && e.parentDuid === parentDuid
  );
  if (isDuplicate) {
    return { isValid: false, error: "Enter a unique option name" };
  }
  return { isValid: true };
};

const onSave = () => {
  if (!titleInput.value || !titleInput.value.isValid) {
    return;
  }
  emit("rename", titleInput.value.value.trim());
  emit("close");

  // TODO turn this on to reorder other cells alphabetically on change
  // actions.visualization.refreshCellsByNode(actions.visualization.getAllRowTasks(), ["tagDuids"]);
};

const rename = () => {
  if (!props.adding) {
    onSave();
  }
};

onMounted(async () => {
  if (titleInput.value && props.adding) {
    await layoutStable(titleInput.value.$el);
    titleInput.value?.focus();
  }
});
</script>

<template>
  <div class="my-1 size-full px-2 pb-2">
    <div class="relative w-full">
      <Input
        ref="titleInput"
        :init-value="option.title"
        :placeholder="adding ? 'Add an option' : 'Rename the option'"
        label="Rename option"
        :input-classes="showButtons ? '!pl-2 !py-0.5 !pr-14' : '!px-2 !py-0.5'"
        class="w-full"
        hide-label
        hide-error
        show-error-as-tooltip
        :validate="invalidOptionTitle"
        @click.stop
        @keydown.stop
        @finalize="rename" />
      <div v-if="showButtons" class="absolute top-1/2 right-1 flex -translate-y-1/2 items-center gap-1">
        <Button
          :btn-style="ButtonStyle.SECONDARY"
          :icon="XIcon"
          :icon-size="IconSize.S"
          class="size-5 !px-0.5"
          text-style="text-xs"
          a11y-label="Cancel"
          @click="emit('close')" />
        <Button
          :btn-style="ButtonStyle.PRIMARY"
          :icon="CheckIcon"
          :icon-size="IconSize.S"
          class="size-5 !px-0.5"
          text-style="text-xs"
          a11y-label="Save"
          :disabled="!titleInput?.isValid"
          @click="onSave" />
      </div>
    </div>
  </div>
</template>
